import { useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { useDonationState } from 'features/payments/hooks/useDonationState';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { WATCHDUTY_SCHEME } from '../../../constants';

const useStyles = makeStyles()(() => ({
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  atag: {
    textDecoration: 'none',
    width: '100%',
  },
}));

const Content = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { fromMobile, clientToken, resetDonationState } = useDonationState();
  const fromMobileRef = useRef<boolean>(fromMobile);
  const clientTokenRef = useRef<string | undefined>(clientToken);

  const isInitialDonation = Boolean(clientTokenRef.current);

  return (
    <>
      <Helmet>
        <title>{t('donationSuccess.title')}</title>
      </Helmet>

      <SimplePageLayout>
        <Box className={classes.contentContainer}>
          <Typography variant="h2" align="center" sx={{ mb: 4 }}>
            {t('donationSuccess.subtitle')}
          </Typography>

          <Typography align="center">{t('donationSuccess.message')}</Typography>
        </Box>

        {/* The react-dom-router link component does not consistently open the custom scheme urls correctly, use explicit A tag */}
        {fromMobileRef.current ? (
          <a
            href={`${WATCHDUTY_SCHEME}app.watchduty.org/?${
              isInitialDonation
                ? `clientToken=${clientTokenRef.current}&transaction=donation`
                : 'donationSuccess=true'
            }`}
            target="_blank"
            className={classes.atag}
            rel="noreferrer"
          >
            <Button
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              onClick={resetDonationState}
            >
              {t('donationSuccess.button')}
            </Button>
          </a>
        ) : (
          <Button
            fullWidth
            size="large"
            sx={{ mt: 2 }}
            component={Link}
            to={
              isInitialDonation
                ? `/?clientToken=${clientTokenRef.current}&transaction=donation`
                : '/'
            }
            replace
            onClick={resetDonationState}
          >
            {t('donationSuccess.button')}
          </Button>
        )}
      </SimplePageLayout>
    </>
  );
};

export const DonationSuccess = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <PageLayout
      data-cname="DonationSuccessPage"
      allowAppBanner={false}
      headerBarProps={{
        action: 'noControls',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
